import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';

import { useRetrySession, useSessionRetryAt } from '../../context/AuthMachine';
import { ErrorFallback } from '../ErrorBoundary';

// ─────────────────────────────────────────────────────────────────────────────

export const SessionError = (props: Props) => {
  const { error } = props;

  const { formatMessage } = useIntl();

  const sessionRetryAt = useSessionRetryAt();
  const retrySession = useRetrySession();

  // ─── State ────────────────────────────────────────────────────────────────

  const [secondsUntilRetry, setSecondsUntilRetry] = useState(() =>
    getSecondsUntilRetry(sessionRetryAt),
  );

  // ─── Derived ──────────────────────────────────────────────────────────────

  const isRetryCoolingDown = sessionRetryAt !== undefined;
  const hasCountdown = secondsUntilRetry > 0;

  const sessionRetryHelperText = hasCountdown
    ? formatMessage(getCountdownMessage(secondsUntilRetry), { seconds: secondsUntilRetry })
    : undefined;

  // ─── Effects ──────────────────────────────────────────────────────────────

  useEffect(() => {
    setSecondsUntilRetry(getSecondsUntilRetry(sessionRetryAt));

    if (!sessionRetryAt) {
      return;
    }

    const intervalId = setInterval(() => {
      setSecondsUntilRetry(getSecondsUntilRetry(sessionRetryAt));
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [sessionRetryAt]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ErrorFallback
      ctaHelperText={sessionRetryHelperText}
      ctaLabel={formatMessage(messages.tryAgain)}
      description={formatMessage(messages.description)}
      error={error}
      isCtaDisabled={isRetryCoolingDown}
      onCtaPress={retrySession}
    />
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getSecondsUntilRetry(sessionRetryAt: number | undefined) {
  if (!sessionRetryAt) {
    return 0;
  }

  return Math.max(0, Math.ceil((sessionRetryAt - Date.now()) / 1000));
}

function getCountdownMessage(secondsUntilRetry: number) {
  if (secondsUntilRetry === 1) {
    return messages.countdownSecond;
  }

  return messages.countdownSeconds;
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly description: NoMessageValues;
  readonly countdownSecond: { readonly seconds: MessageValue };
  readonly countdownSeconds: { readonly seconds: MessageValue };
  readonly tryAgain: NoMessageValues;
}>({
  description: {
    defaultMessage:
      'The app ran into a problem and could not continue. Press the button below to try again.',
    description: 'Session Error | Description',
  },
  countdownSecond: {
    defaultMessage: 'You can try again in {seconds} second.',
    description: 'Session Error | Retry countdown | Second',
  },
  countdownSeconds: {
    defaultMessage: 'You can try again in {seconds} seconds.',
    description: 'Session Error | Retry countdown | Seconds',
  },
  tryAgain: {
    defaultMessage: 'Try again',
    description: 'Session Error | Try again',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  error: Error;
};
